import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import unescape from 'unescape';
import clsx from 'clsx';
import Config, { keyConfig } from '~/config';
import { slugs, urls } from '~/utils';
import themeVariables from '~/shared/themeVariables';

const GallerySimilar = ({
  images,
  serviceType,
  lng,
  t,
  keyword,
  dataSearchImage,
}) => {
  const [numberSimilar, setNumberSimilar] = useState(5);
  const isSilhouette = serviceType === Config.getServiceTypes().SILHOUETTE_AC;
  const isPhotoAC = serviceType === Config.getServiceTypes().PHOTO_AC;
  useEffect(() => {
    const changeNumberSimilar = () => {
      // 306:204(rowHeight) = 3/2 (resolution of image)
      if (window.innerWidth < themeVariables.breakpoints.mobile.maxWidth) {
        setNumberSimilar(6);
      } else {
        setNumberSimilar(Math.ceil(window.innerWidth / 280));
      }
    };

    window.addEventListener('resize', changeNumberSimilar);
    changeNumberSimilar();

    return () => window.removeEventListener('resize', changeNumberSimilar);
  }, []);

  const RenderImage = ({ item }) => {
    const photoTitle = unescape(Config.getTitleByLanguage(item.title, lng));
    const slugTitle = slugs.makeSlug(photoTitle, {
      lang: lng,
    });
    const imageLink = `${Config.getSiteURL(serviceType)}/${keyConfig.get(
      'route_detail_page',
      serviceType,
    )}/${item.id}/${slugTitle}`;
    return (
      <a
        className={clsx('similar-image', {
          'similar-silhouette': isSilhouette,
          'similar-photo': isPhotoAC,
        })}
        href={imageLink}
        target="_blank"
        rel="noreferrer"
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'ImageObject',
              contentUrl: urls.fixImageURL(item.thumbnail),
              license: `${Config.getSiteURL()}/terms`,
              acquireLicensePage: `${Config.renderDownloadPageByServiceAndLanguage(
                lng,
                `${item.id}/${slugTitle}`,
              )}`,
              name: t(Config.getPrefixContent('title_download_page_seo'), {
                title: photoTitle,
                id: item.id,
              }),
              caption: t(Config.getPrefixContent('title_download_page_seo'), {
                title: photoTitle,
                id: item.id,
              }),
              isAccessibleForFree: true,
              isFamilyFriendly: true,
              representativeOfPage: true,
            }),
          }}
        />
        {/* <meta
          itemProp="contentUrl"
          content={urls.fixImageURL(item.thumbnail)}
        />
        <meta
          itemProp="name"
          content={t(Config.getPrefixContent('title_download_page_seo'), {
            title: photoTitle,
            id: item.id,
          })}
        />
        <meta
          itemProp="caption"
          content={t(Config.getPrefixContent('title_download_page_seo'), {
            title: photoTitle,
            id: item.id,
          })}
        /> */}
        <img
          data-src={item.thumbnail}
          alt={item.title?.title_en}
          className="lazyload"
        />
      </a>
    );
  };
  const link = dataSearchImage?.query?.image_token
    ? `${Config.getSiteURL(serviceType)}/search-result?image_token=${
        dataSearchImage?.query?.image_token
      }`
    : `${Config.getSiteURL(
        serviceType,
      )}/search/${keyword}?page=1&color=all&modelCount=-2&orderBy=recent_popular&shape=all`;
  return (
    <div className="gallery-similar-wrapper">
      {images
        .slice(
          0,
          numberSimilar > images.length ? images.length - 1 : numberSimilar - 1,
        )
        .map((item, index) => (
          <RenderImage key={index} item={item} />
        ))}
      {images[images.length - 1] && (
        <a
          className={clsx('last-similar-image', { 'similar-photo': isPhotoAC })}
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          <img
            className={clsx('lazyload', {
              'similar-silhouette': isSilhouette,
              'similar-photo': isPhotoAC,
            })}
            data-src={images[images.length - 1].thumbnail}
            alt={images[images.length - 1].title?.title_en}
          />
          <span className="view-all-image">
            {/* {t(`${Config.getPrefix(serviceType)}_view_all_title`, { keyword })} */}
            {t('view_all')}
          </span>
        </a>
      )}

      <style jsx global>{`
        .gallery-similar-wrapper {
          display: grid;
          grid-column: 1 / ${numberSimilar};
          grid-template-columns: repeat(${numberSimilar}, 1fr);
          width: 100%;
          grid-gap: 10px;
          margin: 12px 0;
        }
        .last-similar-image,
        .similar-image {
          width: 100%;
          height: 160px;
          position: relative;
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid rgba(122, 122, 122, 0.5);
        }

        .similar-silhouette {
          border: 1px solid #5d5d5d;
        }

        .similar-silhouette,
        .similar-silhouette:hover {
          mix-blend-mode: hard-light;
        }

        .similar-image:hover {
          opacity: 0.9;
        }

        .similar-image img,
        .last-similar-image img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          background-color: #ffffff;
        }

        .similar-photo img {
          object-fit: cover;
        }
        .view-all-image {
          top: 0;
          left: 0;
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          background: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.68),
            rgba(0, 0, 0, 0.68)
          );
          font-weight: 600;
          font-size: 16px;
          line-height: 20px;
          color: #ffffff;
        }
        @media (max-width: ${themeVariables.breakpoints.mobile.maxWidth}px) {
          .gallery-similar-wrapper {
            position: relative;
            overflow-x: scroll;
            display: flex;
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
            -webkit-scroll-snap-points-x: repeat(240px);
            -ms-scroll-snap-points-x: repeat(240px);
            scroll-snap-points-x: repeat(240px);
            -webkit-scroll-snap-type: mandatory;
            -ms-scroll-snap-type: mandatory;
            scroll-snap-type: mandatory;
          }
          .last-similar-image,
          .similar-image {
            width: 240px;
            margin-right: 10px;
            flex-shrink: 0;
          }
        }
      `}</style>
    </div>
  );
};

GallerySimilar.propTypes = {
  images: PropTypes.array.isRequired,
  prepareLoad: PropTypes.bool,
  serviceType: PropTypes.string,
  t: PropTypes.any,
  lng: PropTypes.string,
  keyword: PropTypes.string,
};

GallerySimilar.defaultProps = {
  id: 'GallerySimilarIllust',
  margin: 5,
};

export default GallerySimilar;
